import { useEffect, useRef } from 'react';

import { TOUR_ANCHOR_ATTR } from '@bookora/ui';

import { useSessionStore } from '@/auth/token-store';

import { readHostOpens, writeHostOpens } from './storage';
import { reportProgress, reportStruggle, sectionKeyOf } from './struggle/store';
import { useTourStore } from './tour-store';
import type { InlineHostKey } from './tours/types';

/** The hook instance that currently owns the host slot. */
let owner: object | null = null;

/** An open shorter than this is not counted as an abandon. */
const ABANDON_MIN_OPEN_MS = 1_500;

/** Hosts used for real during their current open (`noteInlineHostUse`). */
const usedThisOpen = new Set<InlineHostKey>();

/**
 * Registers the inline tour's host while `open`; `ready` = its data has
 * loaded. Every open is counted on this device: the auto-offer never comes on
 * the FIRST open, which is usually with a patient in the chair.
 *
 * An open that closes WITHOUT the form being used (`noteInlineHostUse`) is
 * reported to the struggle detector as an abandon (critic C6), with the tour
 * anchor of the last field the person was in — the step a help offer starts
 * at. Nothing else about the form is kept: no values, no text.
 *
 * One host at a time: the session form is mounted by two routes, and a
 * second registration replaces the first (with a warning in development).
 */
export function useInlineTour(key: InlineHostKey, opts: { open: boolean; ready: boolean }): void {
  const { open, ready } = opts;
  const setInlineHost = useTourStore((s) => s.setInlineHost);
  const counted = useRef(false);
  const lastAnchor = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      counted.current = false;
      return;
    }
    if (!counted.current) {
      counted.current = true;
      usedThisOpen.delete(key);
      lastAnchor.current = null;
      const { user, activeClinicId } = useSessionStore.getState();
      const userId = user?.id ?? null;
      writeHostOpens(userId, activeClinicId, key, readHostOpens(userId, activeClinicId, key) + 1);
    }
  }, [open, key]);

  // Where in the form the person was: the closest tour anchor of the last
  // focused field inside a dialog (the form's drawer). An id, never content.
  useEffect(() => {
    if (!open) return;
    const openedAt = Date.now();
    const onFocusIn = (event: FocusEvent): void => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[role="dialog"]')) return;
      const anchored = target.closest(`[${TOUR_ANCHOR_ATTR}]`);
      if (anchored) lastAnchor.current = anchored.getAttribute(TOUR_ANCHOR_ATTR);
    };
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      // Closed (or unmounted) without a real use: one abandon. An open shut
      // within a moment is a misclick (or StrictMode's rehearsal), not a
      // struggle.
      if (usedThisOpen.has(key) || Date.now() - openedAt < ABANDON_MIN_OPEN_MS) return;
      const { pathname } = useTourStore.getState();
      reportStruggle({
        type: 'form-abandon',
        section: sectionKeyOf(pathname),
        host: key,
        anchor: lastAnchor.current,
      });
    };
  }, [open, key]);

  const token = useRef({});
  useEffect(() => {
    if (!open) return;
    if (owner && owner !== token.current && import.meta.env.DEV) {
      console.warn(`[guidance] a second «${key}» host registered; it replaces the first.`);
    }
    owner = token.current;
    setInlineHost({ key, open: true, ready });
  }, [open, ready, key, setInlineHost]);

  useEffect(() => {
    if (open || owner !== token.current) return;
    owner = null;
    setInlineHost({ key, open: false, ready: false });
  }, [open, key, setInlineHost]);

  useEffect(() => {
    const mine = token.current;
    return () => {
      if (owner !== mine) return;
      owner = null;
      setInlineHost(null);
    };
  }, [setInlineHost]);
}

/**
 * The host form was used for real (e.g. a session recorded): feeds the
 * auto-offer rule, and tells the struggle detector this open was not an
 * abandon — the form's abandon count starts afresh.
 */
export function noteInlineHostUse(key: InlineHostKey): void {
  const { user, activeClinicId } = useSessionStore.getState();
  const userId = user?.id ?? null;
  usedThisOpen.add(key);
  reportProgress({ host: key });
  // A real use proves at least one open, even from before this counter existed.
  if (readHostOpens(userId, activeClinicId, key) < 1)
    writeHostOpens(userId, activeClinicId, key, 1);
}

/**
 * Any save that went through (the query cache's `onSuccess`): progress for
 * the struggle detector, and — when an inline host form is open — proof that
 * this open was used, not abandoned. A host that forgets `noteInlineHostUse`
 * (a new form mounting `useInlineTour`) then still never reports its own
 * successful saves as abandons; at worst a save of something else from inside
 * the form makes one real abandon go uncounted, which errs on the quiet side.
 */
export function noteMutationSuccess(): void {
  reportProgress();
  const host = useTourStore.getState().inlineHost;
  if (host?.open) usedThisOpen.add(host.key);
}
